import React, { useState, useEffect } from "react";
import { X, Smartphone } from "lucide-react";
import { changeCampaignWhatsappAccount } from "../apis/campaign";
import { toast } from "react-toastify";

/**
 * Change WhatsApp Account Modal
 * Allows user to change the WhatsApp account for a campaign
 */
const ChangeWhatsappAccountModal = ({ isOpen, onClose, campaign, whatsappAccounts, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  useEffect(() => {
    if (isOpen && campaign) {
      // Set current WhatsApp account as default
      setSelectedAccountId(campaign.whatsapp_account_id?.toString() || "");
    }
  }, [isOpen, campaign]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedAccountId) {
      toast.error("Please select a WhatsApp account");
      return;
    }

    if (selectedAccountId === campaign.whatsapp_account_id?.toString()) {
      toast.info("This WhatsApp account is already selected");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("userAccessToken");
      const response = await changeCampaignWhatsappAccount(
        token,
        campaign.campaign_id,
        parseInt(selectedAccountId)
      );

      if (response.data.success) {
        toast.success(response.data.message || "WhatsApp account changed successfully");
        onSuccess?.();
        onClose();
      } else {
        toast.error(response.data.message || "Failed to change WhatsApp account");
      }
    } catch (error) {
      console.error("Error changing WhatsApp account:", error);
      const errorMessage = error.response?.data?.error || "Failed to change WhatsApp account";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedAccount = whatsappAccounts.find(
    (acc) => acc.id?.toString() === selectedAccountId
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Change WhatsApp Account</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-700 mb-4">
              Change the WhatsApp account for <strong>{campaign.campaign_name}</strong>.
            </p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-yellow-900">
                <strong>Note:</strong> You can only change the WhatsApp account for campaigns that
                are scheduled, cancelled, aborted, or pending.
              </p>
            </div>

            {/* Current Account Info */}
            {campaign.phone_number && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
                <p className="text-xs font-medium text-gray-700 mb-1">Current Account:</p>
                <p className="text-xs text-gray-600">{campaign.phone_number}</p>
              </div>
            )}
          </div>

          {/* WhatsApp Account Select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select WhatsApp Account <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Select WhatsApp Account --</option>
              {whatsappAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.phone_number} {account.business_name ? `(${account.business_name})` : ""}
                </option>
              ))}
            </select>
            {selectedAccount && (
              <p className="text-xs text-gray-500 mt-1">
                Selected: {selectedAccount.phone_number}
                {selectedAccount.business_name && ` - ${selectedAccount.business_name}`}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedAccountId}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Changing...
                </>
              ) : (
                "Change Account"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangeWhatsappAccountModal;

